import type { PropertyValuePayload } from './property-value.js';
import { isPropertyValuePayload, propertyScalar } from './property-value.js';

export const AGGREGATE_FUNCTIONS = [
  'count',
  'count_values',
  'count_unique',
  'count_empty',
  'count_not_empty',
  'percent_empty',
  'percent_not_empty',
  'sum',
  'average',
  'min',
  'max',
  'range',
  'earliest',
  'latest',
  'percent_checked',
] as const;

export type AggregateFunction = (typeof AGGREGATE_FUNCTIONS)[number];

export type AggregateResult = {
  value: number | string | boolean | null;
  display: string;
};

const AGGREGATE_LABELS: Record<AggregateFunction, string> = {
  count: 'Count all',
  count_values: 'Count values',
  count_unique: 'Count unique values',
  count_empty: 'Count empty',
  count_not_empty: 'Count not empty',
  percent_empty: 'Percent empty',
  percent_not_empty: 'Percent not empty',
  sum: 'Sum',
  average: 'Average',
  min: 'Min',
  max: 'Max',
  range: 'Range',
  earliest: 'Earliest',
  latest: 'Latest',
  percent_checked: 'Percent checked',
};

/** User-facing label for an aggregate function. */
export function aggregateFunctionLabel(fn: AggregateFunction): string {
  return AGGREGATE_LABELS[fn];
}

export function isAggregateFunction(value: string): value is AggregateFunction {
  return (AGGREGATE_FUNCTIONS as readonly string[]).includes(value);
}

function isEmptyPayload(payload: PropertyValuePayload | null | undefined): boolean {
  if (!payload) {
    return true;
  }
  const scalar = propertyScalar(payload);
  if (scalar === null) {
    return true;
  }
  if (typeof scalar === 'string' && scalar === '') {
    return true;
  }
  if (payload.t === 'relation' || payload.t === 'multi_select') {
    return !Array.isArray(payload.v) || payload.v.length === 0;
  }
  return false;
}

function numericFromPayload(payload: PropertyValuePayload): number | null {
  if (payload.t !== 'number') {
    return null;
  }
  const n = typeof payload.v === 'number' ? payload.v : Number(payload.v);
  return Number.isFinite(n) ? n : null;
}

function dateFromPayload(payload: PropertyValuePayload): string | null {
  if (payload.t !== 'date') {
    return null;
  }
  const start = payload.v == null ? null : String(payload.v).trim();
  return start || null;
}

function uniqueKey(payload: PropertyValuePayload): string {
  if (payload.t === 'multi_select' && Array.isArray(payload.v)) {
    return [...payload.v].sort().join(',');
  }
  if (payload.t === 'relation' && Array.isArray(payload.v)) {
    return [...payload.v].sort().join(',');
  }
  const scalar = propertyScalar(payload);
  if (scalar === null) {
    return '';
  }
  return String(scalar);
}

function formatPercent(n: number): string {
  const rounded = Math.round(n * 10) / 10;
  return `${rounded}%`;
}

function formatNumber(n: number): string {
  if (Number.isInteger(n)) {
    return String(n);
  }
  const rounded = Math.round(n * 100) / 100;
  return String(rounded);
}

/** Which aggregate functions apply to a stored column type (for footers / rollup target). */
const COUNT_AGGREGATES: AggregateFunction[] = [
  'count',
  'count_values',
  'count_unique',
  'count_empty',
  'count_not_empty',
];

const PERCENT_AGGREGATES: AggregateFunction[] = [
  'percent_empty',
  'percent_not_empty',
];

export type AggregateMenuGroupId = 'count' | 'percent' | 'more';

export type AggregateMenuGroup = {
  id: AggregateMenuGroupId;
  label: string;
  functions: AggregateFunction[];
};

/** Notion-style Calculate menu groups for a column type. */
export function aggregateMenuGroupsForColumnType(
  columnType: string,
): AggregateMenuGroup[] {
  const available = new Set(aggregateFunctionsForColumnType(columnType));
  const groups: AggregateMenuGroup[] = [];

  const countFns = COUNT_AGGREGATES.filter((f) => available.has(f));
  if (countFns.length > 0) {
    groups.push({ id: 'count', label: 'Count', functions: countFns });
  }

  const percentFns = [
    ...PERCENT_AGGREGATES,
    ...(columnType === 'checkbox' ? (['percent_checked'] as const) : []),
  ].filter((f) => available.has(f));
  if (percentFns.length > 0) {
    groups.push({ id: 'percent', label: 'Percent', functions: percentFns });
  }

  const grouped = new Set([...countFns, ...percentFns]);
  const moreFns = aggregateFunctionsForColumnType(columnType).filter(
    (f) => !grouped.has(f),
  );
  if (moreFns.length > 0) {
    groups.push({ id: 'more', label: 'More options', functions: moreFns });
  }

  return groups;
}

export function aggregateFunctionsForColumnType(columnType: string): AggregateFunction[] {
  const universal: AggregateFunction[] = [
    'count',
    'count_values',
    'count_unique',
    'count_empty',
    'count_not_empty',
    'percent_empty',
    'percent_not_empty',
  ];
  switch (columnType) {
    case 'number':
      return [...universal, 'sum', 'average', 'min', 'max', 'range'];
    case 'date':
      return [...universal, 'earliest', 'latest'];
    case 'checkbox':
      return [...universal, 'percent_checked'];
    default:
      return universal;
  }
}

export function defaultAggregateForColumnType(columnType: string): AggregateFunction {
  if (columnType === 'number') {
    return 'sum';
  }
  if (columnType === 'checkbox') {
    return 'percent_checked';
  }
  return 'count';
}

/**
 * Aggregate over cell payloads for one column type.
 * @param rowCount total rows in scope (for count/percent when payload missing)
 */
export function computeAggregate(
  fn: AggregateFunction,
  payloads: (PropertyValuePayload | null | undefined)[],
  columnType: string,
  rowCount?: number,
): AggregateResult {
  const total = rowCount ?? payloads.length;
  const cells = payloads.map((p) =>
    p && isPropertyValuePayload(p) ? p : null,
  );

  const emptyCount = cells.filter((p) => isEmptyPayload(p)).length;
  const nonEmpty = cells.filter((p): p is PropertyValuePayload => !isEmptyPayload(p));

  switch (fn) {
    case 'count':
      return { value: total, display: formatNumber(total) };
    case 'count_values':
      return { value: nonEmpty.length, display: formatNumber(nonEmpty.length) };
    case 'count_unique': {
      const keys = new Set(nonEmpty.map(uniqueKey));
      return { value: keys.size, display: formatNumber(keys.size) };
    }
    case 'count_empty':
      return { value: emptyCount, display: formatNumber(emptyCount) };
    case 'count_not_empty':
      return { value: nonEmpty.length, display: formatNumber(nonEmpty.length) };
    case 'percent_empty': {
      const pct = total === 0 ? 0 : (emptyCount / total) * 100;
      return { value: pct, display: formatPercent(pct) };
    }
    case 'percent_not_empty': {
      const pct = total === 0 ? 0 : (nonEmpty.length / total) * 100;
      return { value: pct, display: formatPercent(pct) };
    }
    case 'sum': {
      const nums = nonEmpty
        .map(numericFromPayload)
        .filter((n): n is number => n !== null);
      const sum = nums.reduce((a, b) => a + b, 0);
      return { value: sum, display: formatNumber(sum) };
    }
    case 'average': {
      const nums = nonEmpty
        .map(numericFromPayload)
        .filter((n): n is number => n !== null);
      if (nums.length === 0) {
        return { value: null, display: '' };
      }
      const avg = nums.reduce((a, b) => a + b, 0) / nums.length;
      return { value: avg, display: formatNumber(avg) };
    }
    case 'min': {
      if (columnType === 'date') {
        const dates = nonEmpty
          .map(dateFromPayload)
          .filter((d): d is string => d !== null)
          .sort();
        const v = dates[0] ?? null;
        return { value: v, display: v ?? '' };
      }
      const nums = nonEmpty
        .map(numericFromPayload)
        .filter((n): n is number => n !== null);
      if (nums.length === 0) {
        return { value: null, display: '' };
      }
      const v = Math.min(...nums);
      return { value: v, display: formatNumber(v) };
    }
    case 'max': {
      if (columnType === 'date') {
        const dates = nonEmpty
          .map(dateFromPayload)
          .filter((d): d is string => d !== null)
          .sort();
        const v = dates[dates.length - 1] ?? null;
        return { value: v, display: v ?? '' };
      }
      const nums = nonEmpty
        .map(numericFromPayload)
        .filter((n): n is number => n !== null);
      if (nums.length === 0) {
        return { value: null, display: '' };
      }
      const v = Math.max(...nums);
      return { value: v, display: formatNumber(v) };
    }
    case 'range': {
      const nums = nonEmpty
        .map(numericFromPayload)
        .filter((n): n is number => n !== null);
      if (nums.length === 0) {
        return { value: null, display: '' };
      }
      const v = Math.max(...nums) - Math.min(...nums);
      return { value: v, display: formatNumber(v) };
    }
    case 'earliest': {
      const dates = nonEmpty
        .map(dateFromPayload)
        .filter((d): d is string => d !== null)
        .sort();
      const v = dates[0] ?? null;
      return { value: v, display: v ?? '' };
    }
    case 'latest': {
      const dates = nonEmpty
        .map(dateFromPayload)
        .filter((d): d is string => d !== null)
        .sort();
      const v = dates[dates.length - 1] ?? null;
      return { value: v, display: v ?? '' };
    }
    case 'percent_checked': {
      const checked = nonEmpty.filter((p) => p.t === 'checkbox' && p.v === true).length;
      const pct = total === 0 ? 0 : (checked / total) * 100;
      return { value: pct, display: formatPercent(pct) };
    }
    default:
      return { value: null, display: '' };
  }
}

/** Encode a rollup cell value for injection into propertiesByRowId. */
export function encodeRollupCellValue(result: AggregateResult): PropertyValuePayload | null {
  if (result.value === null && result.display === '') {
    return null;
  }
  return { t: 'rollup', v: result.value, display: result.display };
}
